<template>
  <slot></slot>
  <modal :id="modalId" :title="title" @shown="onModalShown">
    <div>

      <div v-if="error" class="alert alert-danger text-center">
        {{ error }}
      </div>

      <template v-if="deletion">
        <p>Are you sure you want to delete <em>'{{ data.name }}'</em> rule?</p>
        <small class="text-danger" v-if="errors.ondelete">{{ errors.ondelete[0] }}</small>
      </template>
      <template v-else>

        <!-- Name -->
        <div class="form-group row">
          <label for="text" class="col-sm-3 col-form-label">Rule Name</label>
          <div class="col-sm-9">
            <input type="text" id="text" class="form-control form-control-sm" required="required"
                   v-model="data.name" placeholder="Unique rule name...">
            <div class="text-muted"><small>Give this rule a unique name so you can easily distinguish it from others.</small></div>
            <small v-if="errors && errors.name" class="text-danger">{{ errors.name[0] }}</small>
          </div>
        </div>

        <!-- Event -->
        <div class="form-group row">
          <label for="event" class="col-sm-3 col-form-label">Event</label>
          <div class="col-sm-9">
            <select id="event" class="form-control" v-model="data.event" @change="updateEventConfig">
              <option v-for="(eventTitle, event) in events" :value="event">{{ eventTitle }}</option>
            </select>
            <div class="text-muted"><small>Choose when a task should be created based on this rule.</small></div>
            <small v-if="errors && errors.event" class="text-danger">{{ errors.event[0] }}</small>
          </div>
        </div>

        <!-- Event Config -->
        <div v-if="eventFields[data.event]">

          <div v-for="(field, fieldName) in eventFields[data.event]" class="form-group row">
            <label for="event" class="col-sm-3 col-form-label">{{ field.name }}</label>
            <div class="col-sm-9">

              <chosen v-if="field.type === 'lookup'"
                      :id="fieldName" :options="field.options" :multiple="true" :allow-empty="false"
                      placeholder="Select options..."
                      class="form-control form-control-sm" v-model="eventConfig[fieldName]"
              ></chosen>

              <input v-else type="text" :id="fieldName"
                     class="form-control form-control-sm"
                     v-model="eventConfig[fieldName]">

              <div v-if="field.description" class="text-muted"><small v-html="field.description"></small></div>
              <small v-if="errors && errors['event_config.' + fieldName]" class="text-danger">{{ errors['event_config.' + fieldName][0] }}</small>
            </div>
          </div>

        </div>

        <!-- Claim Filter -->
        <div class="form-group row">
          <label for="event" class="col-sm-3 col-form-label">Claim Filter</label>
          <div class="col-sm-9">
            <select id="claim_filter" class="form-control" v-model="data.claim_filter">
              <option value="">No filter, tasks may be created on any claim</option>
              <option value="saved" v-if="data.saved_filter">Keep saved filter</option>
              <option v-if="currentUserFilter" value="current">Import current user filter</option>
            </select>
            <div v-if="currentUserFilter && (data.claim_filter === 'current')" class="alert alert-info mb-1 mt-1" v-html="currentUserFilter"></div>
            <div v-if="data.claim_filter === 'saved'" class="alert alert-info mb-1 mt-1" v-html="data.saved_filter"></div>
            <div class="text-muted"><small>Choose to either create tasks on any claim or restrict them to those fitting selected filter settings.</small></div>
            <small v-if="errors && errors.claim_filter" class="text-danger">{{ errors.claim_filter[0] }}</small>
          </div>
        </div>

        <!-- Task -->
        <div class="form-group row">
          <label for="task_type" class="col-sm-3 col-form-label">Task</label>
          <div class="col-sm-9">
            <select id="task_type" class="form-control" v-model="data.task_type_id">
              <option v-for="(taskType, typeId) in types" :value="typeId">{{ taskType }}</option>
            </select>
            <div class="text-warning"><small>Please note, the task won't be generated if the selected task option type (TPA/non-TPA) is inconsistent with the claim's associated client.</small></div>
            <small v-if="errors && errors.task_type_id" class="text-danger">{{ errors.task_type_id[0] }}</small>
          </div>
        </div>

        <!-- Custom task text -->
        <div v-if="showCustomTextField" class="form-group row">
          <label for="text" class="col-sm-3 col-form-label">Task Text</label>
          <div class="col-sm-9">
            <textarea class="form-control" v-model="data.text" rows="5"></textarea>
            <small v-if="errors && errors.text" class="text-danger">{{ errors.text[0] }}</small>
          </div>
        </div>

        <!-- Due date -->
        <div class="form-group row">
          <label class="col-sm-3 col-form-label">Due Date</label>
          <div class="col-sm-9">

            <label class="mr-2 mt-2">
              <input type="checkbox" id="status" v-model="data.is_relative_due_date">
              Relative
            </label>

            <template v-if="data.is_relative_due_date">
              <div class="input-group input-group-sm">
                <input type="number" id="due_date_days" min="0" class="form-control form-control-sm input-sm" required="required"
                       v-model="data.due_date_days" placeholder="Days">

                <div class="input-group-append" data-toggle="buttons">
                  <button v-for="relOption in relDayOptions" class="btn btn-info btn-sm" @click="setDueDays(relOption.days)">
                    <input type="radio"> {{ relOption.text }}
                  </button>
                </div>

              </div>
              <div class="text-muted"><small>Please enter the number of days from the date of the event to calculate the due date.</small></div>
              <small v-if="errors && errors.due_date_days" class="text-danger">{{ errors.due_date_days[0] }}</small>
            </template>

            <template v-if="!data.is_relative_due_date">
              <div v-if="expiredDueDate">
                <strong class="text-danger">{{ expiredDueDate }}</strong><br>
                <small class="text-warning">The saved due date has already passed. Please select a new date for the rule to function.</small>
              </div>
              <div>
                <div class="mb-1">
                  <datepicker
                    v-model="data.due_date"
                    ref="datepicker"
                    :min-date="minDate"
                    :disabled="!enableDatePicker"
                    @closed="selectDate()"
                    placeholder="Select a date">
                  </datepicker>
                </div>
                <div class="btn-group btn-group-sm d-flex" data-toggle="buttons">
                  <button v-for="days in dayOptions" class="btn btn-info btn-sm" @click="addDays(days)">
                    <input type="radio" name="days"> {{ days }} days
                  </button>
                  <button class="btn btn-info btn-sm" @click="activateDatePicker()">
                    <input type="radio" name="days"> Other
                  </button>
                </div>

              </div>
              <div class="text-warning"><small>Please note, if the chosen date isn't in the future at the time of task creation, the task won't be generated.</small></div>
              <small v-if="errors && errors.due_date" class="text-danger">{{ errors.due_date[0] }}</small>
            </template>
          </div>
        </div>

        <!-- Assignees -->
        <div class="form-group row">
          <label for="task_type" class="col-sm-3 col-form-label">Assignees</label>
          <div class="col-sm-9">

            <table class="table">
              <tr v-for="(userTypeLabel, userType) in userTypes">
                <td class="td-small" colspan="2">
                  <a class="btn btn-xs btn-outline-secondary btn-iconbox" @click="toggleUserTypeSelection(userType)"><i :class="'fa' + (userTypeIndex(userType) === -1 ? '' : ' fa-check')"></i></a>
                  <span class="pointered" @click="toggleUserTypeSelection(userType)">{{ userTypeLabel }}</span>
                </td>
              </tr>

              <tr v-for="(user, index) in customUsers">
                <td class="td-small">
                  <a class="btn btn-xs btn-outline-secondary btn-iconbox" @click="removeUser(user)"><i class="fa fa-close"></i></a>
                  <span class="pointered" @click="removeUser(user)">Selected user #{{index+1}}</span>
                </td>
                <td>
                  <a :href="user.link" target="_blank">{{ user.name }}</a>
                </td>
              </tr>

              <tr v-if="errors && (errors.assignees_by_type || errors.assignees_by_id)">
                <td colspan="2">
                  <small class="text-danger">{{ errors.assignees_by_type[0] || errors.assignees_by_id[0] }}</small>
                </td>
              </tr>

              <tr>
                <td colspan="2">
                  <div class="text-warning"><small>Please note, the task won't be generated if the selected claim role isn't assigned at the time of task creation.</small></div>
                  <user-typeahead v-model="newUser" @userSelected="addNewUser"
                                  placeholder="Find a user by name or email address and add to assignees..."
                                  class="pt-0 mt-0"></user-typeahead>
                </td>
              </tr>
            </table>

          </div>
        </div>

        <!-- Status -->
        <div class="form-group row">
          <label class="col-sm-3 col-form-label">Rule Status</label>
          <div class="col-sm-9">
            <label class="mr-2 mt-2">
              <input type="checkbox" id="status" v-model="data.status">
              Active
            </label>
            <br>
            <template v-if="errors && errors.status">
              <small class="text-danger">{{ errors.status[0] }}</small>
              <br>
            </template>
          </div>
        </div>

      </template>
    </div>

    <template #footer>
      <template v-if="deletion">
        <button type="button" class="btn btn-default" @click="cancelDeletion"><i class="fa fa-fw fa-ban"></i> Cancel</button>
        <button type="button" class="btn btn-danger" @click="submit"><i class="fa fa-fw fa-trash"></i> Delete</button>
      </template>
      <template v-else>
        <button type="button" class="btn btn-danger mr-auto" v-if="isEdit" @click="prepareDeletion"><i class="fa fa-fw fa-trash"></i> Delete</button>
        <button type="button" class="btn btn-default" @click="hideModal"><i class="fa fa-fw fa-ban"></i> Close</button>
        <button type="button" class="btn btn-primary" :disabled="saving" @click="submit"><i class="fa fa-fw fa-upload"></i> Save changes</button>
      </template>
    </template>

  </modal>

</template>

<script>
import userTypeahead from "../UserTypeahead.vue";
import HasModal from "./../mixins/HasModal";

const DATE_FORMAT = 'MM/DD/YYYY';

export default {
  mixins: [HasModal],

  components: {
    userTypeahead
  },

  props: {

    id: {
      type: Number,
      default: 0
    },
    modalId: {
      type: String,
      default: 'task-rule-modal'
    },
    initialData: {
      type: Object,
      default: () => {
        return {
          id: 0,
          name: '',
          event: '',
          event_config: {},
          claim_filter: '',
          saved_filter: null,
          task_type_id: null,
          text: '',
          is_relative_due_date: true,
          due_date_days: '',
          due_date: '',
          expired: false,
          assignees: [],
          assignees_by_type: [],
          assignees_by_id: [],
          status: 1,
        };
      }
    },
    events: {
      type: Object
    },
    eventFields: {
      type: Object,
      default: () => {}
    },
    currentUserFilter: {
      type: String,
      default: ''
    },
    taskTypes: {
      type: Object,
      default: () => {}
    },
    tpaTaskTypes: {
      type: Array,
      default: () => []
    },
    userTypes: {
      type: Object,
      default: () => {}
    },
  },

  data() {

    return {
      data: this.initialData,
      eventConfig: {},
      dayOptions: [7, 14, 21, 30, 45, 60],
      enableDatePicker: false,
      expiredDueDate: '',
      relDayOptions: [
        {days: 0, text: 'On the same day'},
        {days: 7, text: 'In 7 days'},
        {days: 14, text: 'In 14 days'},
        {days: 30, text: 'In 30 days'},
        {days: 45, text: 'In 45 days'},
        {days: 60, text: 'In 60 days'},
      ],
      customUsers: [],
      newUser: {},
      error: '',
      errors: {},
      isValid: true,
      deletion: false,
      saving: false,
    };

  },

  mounted() {
    this.loadSelectedUsers();

    if (this.isNew && Object.keys(this.events).length === 1) {
      this.data.event = Object.keys(this.events)[0];
    }
    if (this.isEdit) {
      this.updateEventConfig();
    }

    if (this.data.saved_filter) {
      this.data.claim_filter = 'saved';
    }
    if (this.data.claim_filter === null || this.data.claim_filter === undefined) this.data.claim_filter = '';

    this.expiredDueDate = this.data.expired ? this.data.due_date : '';

    this.error = '';
    this.errors = {};

    if (this.isNew) {
      this.deletion = false;
    }
  },

  computed: {
    title() {
      if (this.deletion) return "Delete Task Rule";
      return this.isNew ? "Add Task Rule" : "Edit Task Rule";
    },
    method() {
      if (this.deletion) return "DELETE";
      return this.isNew ? "POST" : "PATCH";
    },
    actionUrl() {
      if (this.deletion) return '/admin/claim-task-settings/rules/' + this.data.id;
      return this.isNew ? '/admin/claim-task-settings/rules' : '/admin/claim-task-settings/rules/' + this.data.id;
    },
    isEdit() {
      return this.data.id > 0;
    },
    isNew() {
      return !this.isEdit;
    },
    // Task types and "other" option.
    types() {
      let types = this.taskTypes;
      Object.keys(types).forEach(id => {
        types[id] = this.tpaTaskTypes.includes(parseInt(id)) ? 'TPA: ' + types[id] : types[id];
      });
      types[''] = 'Other';
      return types;
    },
    // Show custom text field if "Other" task type selected.
    showCustomTextField() {
      return this.data.task_type_id === '';
    },
    minDate() {
      return moment(new Date()).set({hour:0,minute:0,second:0,millisecond:0}).format(DATE_FORMAT);
    }
  },

  methods: {
    updateEventConfig() {
      let config = {};
      if (this.eventFields[this.data.event]) {
        Object.keys(this.eventFields[this.data.event]).forEach(fieldName => {
          let value = null;
          let type = this.eventFields[this.data.event][fieldName].type;
          if (type === 'lookup') value = [];

          if (this.isEdit) {
            if (this.data.event_config) {
              if (this.data.event_config[fieldName]) {
                if (this.data.event_config[fieldName] !== undefined) {
                  value = this.data.event_config[fieldName];
                }
              }
            }
          }

          config[fieldName] = value;
        });
      }
      this.eventConfig = config;
    },

    loadSelectedUsers() {
      if (!this.isEdit) return;

      this.customUsers = [];
      this.data.assignees.forEach(a => {
        this.customUsers.push(a);
      });
    },

    addNewUser() {
      if (this.newUser) {
        let userIndex = this.customUsers.findIndex(a => a.id === this.newUser.id);
        if (userIndex === -1) {
          this.customUsers.push({
            id: this.newUser.id,
            name: (this.newUser.first_name + ' ' + this.newUser.last_name).trim(),
            link: 'users/' + this.newUser.id,
          });
          this.data.assignees_by_id.push(this.newUser.id);
        }
        this.newUser = {};
      }
    },

    removeUser(user) {
      const index = this.customUsers.findIndex(a => a.id === user.id);
      if (index !== -1) {
        this.customUsers.splice(index, 1);
        this.data.assignees_by_id.splice(index, 1);
      }
    },

    toggleUserTypeSelection(userType) {
      const index = this.userTypeIndex(userType);
      if (index === -1) {
        this.data.assignees_by_type.push(userType);
      } else {
        this.data.assignees_by_type.splice(index, 1);
      }
    },

    userTypeIndex(userType) {
      let selectedTypes = this.data.assignees_by_type || [];
      return selectedTypes.indexOf(userType)
    },

    setDate(date) {
      if (date) {
        this.date = moment(date, DATE_FORMAT).format(DATE_FORMAT);
        this.data.due_date = date;
        this.enableDatePicker = false;
      }
    },
    selectDate() {
      this.setDate(this.data.due_date);
    },
    addDays(days) {
      var calculatedDate = moment(new Date()).add(days, 'days').format(DATE_FORMAT);
      this.setDate(calculatedDate);
      this.enableDatePicker = false;
    },
    activateDatePicker() {
      this.enableDatePicker = true;
      this.$nextTick(function () {
        this.$refs.datepicker.openMenu();
      });
    },
    setDueDays(days) {
      this.data.due_date_days = days;
    },

    /**
     * React on modal @shown event.
     */
    onModalShown() {
      this.error = '';
      this.errors = {};
      this.isValid = true;
    },

    /**
     * Submit create/update/delete.
     */
    submit() {
      this.validate();
      if (!this.isValid) return;

      this.saving = true;

      let formData = this.formData();
      axios.post(this.actionUrl, formData)
          .then(response => {
            window.location.reload();
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to complete the action.');
            this.saving = false;
          });
    },

    /**
     * Validate form.
     */
    validate() {
      this.error = '';
      this.errors = {};
      this.isValid = true;
      this.validateForm();
    },

    /**
     * Validate task form.
     */
    validateForm() {
      if (this.data.name.trim() === '') {
        this.setError('name', 'Please specify a rule name.');
      }
      if (!this.data.event) {
        this.setError('event', 'Please specify an event.');
      }
      if ((this.data.task_type_id === undefined) || (this.data.task_type_id === null)) {
        this.setError('task_type_id', 'Please select a task.');
      }
      if (this.data.task_type_id === '') {
        if (this.data.text.trim() === '') {
          this.setError('text', 'Please specify a task text.');
        }
      }
      if (this.data.is_relative_due_date) {
        if (this.data.due_date_days === '') {
          this.setError('due_date_days', 'Please enter the number of days.');
        }
      } else {
        if (!this.data.due_date) {
          this.setError('due_date', 'Please specify a due date.');
        }
      }
      if ((this.data.assignees_by_type.length + this.data.assignees_by_id.length) === 0) {
        this.setError('assignees_by_type', 'Please select one or more assignees.');
      }
    },

    /**
     * Set validation error.
     */
    setError(field, message) {
      if (!this.errors[field]) this.errors[field] = [];
      this.errors[field].push(message);
      this.isValid = false;
    },

    /**
     * Prepare form data.
     */
    formData() {
      const formData = new FormData();

      Object.keys(this.data).forEach(fieldName => {
        const value = this.data[fieldName] ?? '';
        if (Array.isArray(this.data[fieldName])) {
          value.forEach(item => {
            formData.append(fieldName + '[]', item);
          })
        } else {
          formData.append(fieldName, value);
        }
      });
      formData.set('event_config', JSON.stringify(this.eventConfig));

      if (!formData.get('task_type_id')) {
        formData.delete('task_type_id');
      } else {
        formData.set('text', "");
      }

      if (this.data.is_relative_due_date) {
        formData.delete('due_date');
      } else {
        formData.delete('due_date_days');
      }

      if (formData.get('assignees[]')) {
        formData.delete('assignees[]');
      }

      formData.append('_method', this.method);
      return formData;
    },

    /**
     * Handle axios errors
     *
     * @param errorObj
     * @param defaultMessage
     */
    handleErrors(errorObj, defaultMessage) {
      if (errorObj.response.status === 422) {
        this.errors = errorObj.response.data.errors || {};
      } else {
        this.error = defaultMessage;
      }
    },

    prepareDeletion() {
      this.deletion = true;
    },

    cancelDeletion() {
      this.deletion = false;
    },
  }
}

</script>

<style scoped>
  .btn:focus, .btn.focus {
    outline: none;
  }

  [data-toggle=buttons] > .btn input[type=radio], [data-toggle=buttons] > .btn-static input[type=radio], [data-toggle=buttons] > .btn input[type=checkbox], [data-toggle=buttons] > .btn-static input[type=checkbox], [data-toggle=buttons] > .btn-group > .btn input[type=radio], [data-toggle=buttons] > .btn-group > .btn input[type=checkbox] {
    position: absolute;
    clip: rect(0, 0, 0, 0);
    pointer-events: none;
  }

  .btn-iconbox {
    padding: 1px;
    width: 20px;
    height: 20px;
    margin-right: 5px;
  }

  .pointered {
    cursor: pointer;
  }
</style>
